import { BehaviorSubject, combineLatest, filter, map } from 'rxjs';
import { Status } from '../enums/status.enum';
import { Company, DisplayConditions, Smartphoto } from '@bdeo-motor/smartphoto-entities';

export class SmartphotoAbilities {
  protected readonly company$ = new BehaviorSubject<Company | undefined>(undefined);
  protected readonly smartphoto$ = new BehaviorSubject<Smartphoto | undefined>(undefined);
  protected readonly settledCompany$ = this.company$.pipe(filter((company): company is Company => Boolean(company)));
  protected readonly settledSmartphoto$ = this.smartphoto$.pipe(filter((smartphoto): smartphoto is Smartphoto => Boolean(smartphoto)));
  protected readonly allSettled$ = combineLatest([this.settledCompany$, this.settledSmartphoto$]);

  setCompany(company: Company): void {
    this.company$.next(company);
  }

  setSmartphoto(smartphoto: Smartphoto): void {
    this.smartphoto$.next(smartphoto);
  }

  readonly showEstimation$ = this.allSettled$.pipe(
    map(([company, smartphoto]) =>
      Boolean(company.brokersInfo?.showEstimation && ![Status.NEW, Status.IN_PROGRESS].includes(smartphoto.status))
    )
  );

  readonly canAddNewDamage$ = combineLatest([this.showEstimation$, this.settledSmartphoto$]).pipe(
    map(([showEstimation, smartphoto]) => showEstimation && smartphoto.status !== Status.CANCELLED)
  );

  readonly canReview$ = this.allSettled$.pipe(
    map(([company, smartphoto]) => {
      if (!company.brokersInfo?.showEstimation) {
        return false;
      }

      if (!DisplayConditions.canShowReview(smartphoto)) {
        return false;
      }

      return company.toReview().includes(smartphoto.status);
    })
  );

  readonly canGoToAdditionals$ = this.settledSmartphoto$.pipe(
    map((smartphoto) => [Status.REVIEWED, Status.BODYSHOP_REOPENED].includes(smartphoto.status))
  );

  readonly showAdditionalsButton$ = combineLatest([this.allSettled$, this.canGoToAdditionals$]).pipe(
    map(([[company, smartphoto], canGoToAdditionals]) => {
      if (!company.smartphoto?.additionals?.active) {
        return false;
      }

      if (!company.brokersInfo?.showGoToAdditionalsButton) {
        return false;
      }

      if (company.smartphoto?.additionals?.allowOnFinished && smartphoto.status === Status.COMPLETED) {
        return true;
      }

      return canGoToAdditionals;
    })
  );

  readonly showDownloadImagesButton$ = this.settledCompany$.pipe(map((company) => !company.brokersInfo?.hideDownloadImagesButton));

  readonly canDownloadPdfReport$ = combineLatest([this.allSettled$, this.showDownloadImagesButton$]).pipe(
    map(([[company, smartphoto], showDownloadImagesButton]) => {
      if (!company.brokersInfo?.showDownloadPdfButton) {
        return false;
      }

      if (!showDownloadImagesButton) {
        return false;
      }

      if (!smartphoto.report) {
        return false;
      }

      return [Status.COMPLETED, Status.REVIEWED, Status.BODYSHOP_REOPENED, Status.REPAIRED].includes(smartphoto.status);
    })
  );

  readonly canTakePictures$ = this.settledSmartphoto$.pipe(
    map((smartphoto) => [Status.NEW, Status.IN_PROGRESS, Status.REOPENED].includes(smartphoto.status))
  );

  readonly canSendPdfReport$ = combineLatest([this.settledSmartphoto$, this.showEstimation$]).pipe(
    map(([smartphoto, showEstimation]) => {
      if (!showEstimation) {
        return false;
      }

      if (!smartphoto.report) {
        return false;
      }

      return [Status.COMPLETED, Status.REVIEWED, Status.BODYSHOP_REOPENED, Status.REPAIRED].includes(smartphoto.status);
    })
  );

  readonly isWaitingForAI$ = this.settledSmartphoto$.pipe(map((smartphoto) => smartphoto.waitingForAI));

  readonly isVlcActive$ = this.settledCompany$.pipe(
    map((company) => Boolean(company.brokersInfo?.vlc?.active && company.brokersInfo?.showEstimation))
  );

  readonly manageDamages$ = this.settledCompany$.pipe(map((company) => company.brokersInfo?.manageDamages));
}
